import React, { useContext, useRef } from 'react';
import { store } from 'components/Store';
import { useContentResults } from 'components/Dashboard/ContentResultsContext';
import { Link } from 'react-router-dom';
import TagsLists from 'components/Dashboard/TagsList';
import useRememberPosition from 'components/Dashboard/hooks/useRememberPosition';
import getUrlParam from 'components/Dashboard/utilities/getUrlParam';
import getAppSite from 'utils/getAppSite';
import time from 'utils/time';
import createMarkup from 'components/Dashboard/helpers/createMarkup';
import useItemController from 'components/Dashboard/ItemController/useItemController';
import GeneralItemActions from 'components/Dashboard/ItemController/GeneralItemActions';
import usePositionBodySnippet from 'components/Dashboard/hooks/usePositionBodySnippet';
import ContentTypeBadge from 'components/Dashboard/components/ContentTypeBadge';
import PriorityBadge from 'components/Dashboard/ItemController/PriorityBadge';
import RatingUpdateBadge from 'components/Dashboard/PriorityUpdates/RatingUpdateBadge';
import TransformFromPascalCase from 'components/Settings/Utilities/TransformFromPascalCase';
import preventLink from 'components/Dashboard/TagsList/prevenLink';

time.normalizeDateLocale();

const LegislationItem = (props) => {
  const globalState = useContext(store);
  const { state } = globalState;
  const { contentResults } = useContentResults();
  const { search } = state;
  const { list, activeSearch } = search;
  const { Legislation } = contentResults;
  const {
    index,
    item,
    scrollBarRef,
    itemOnclick,
    embedShowTags,
    explanations,
    feedItemFlags: propsFeedItemFlags,
    itemFromWidget,
    notShowShare,
    hideYellowDot,
    showContentTypeBadge,
    thumbDownProps,
    tagsFixedPosition,
    itemActionsProps,
    onlyEmbargoedTag,
    array,
    zIndex,
    tooltipText,
    showPriorityBadge,
    setActiveUpdate,
  } = props;
  const feedItemFlags = propsFeedItemFlags ?? Legislation?.feedItemFlags ?? [];
  const titleBodyComponent = useRef(null);
  const { bodySnippet } = usePositionBodySnippet({ titleBodyComponent, item });
  const { unseenConditional, locationDescriptor, priorityBadgeCondition, rateUpdatesMode } = useItemController({
    item,
    index,
    propsFeedItemFlags: props.feedItemFlags,
    hideYellowDot,
    thumbDownProps,
    showPriorityBadge,
  });

  const { rememberPosition } = useRememberPosition();

  return (
    <>
      <li
        className={`my-2 content-item adjustment-unseen-dot-parliamentary`}
        style={{ zIndex: zIndex ?? (array ? array.length - index : 1) }}
      >
        <Link
          to={locationDescriptor(`${item.contentUrl}`)}
          onClick={(e) => {
            if (rateUpdatesMode && setActiveUpdate) {
              preventLink(e);
              setActiveUpdate({ ...item, importanceRating: feedItemFlags?.[index]?.importanceRating });
              return;
            }
            if (scrollBarRef) {
              rememberPosition('Legislation', scrollBarRef, index);
            }
            if (itemOnclick) {
              itemOnclick(e);
            }
          }}
          data-tooltip-content={tooltipText}
          data-tooltip-id={'general-tooltip'}
          data-tooltip-class-name='header-tooltip'
          data-tooltip-place='top'
        >
          {unseenConditional && (
            <>
              <span className='unseen-dot' />
            </>
          )}
          <div className='p-4'>
            <div className='d-flex align-items-baseline'>
              <div className='flex-grow-1 row mx-0 align-items-start legislation-title-container'>
                <div className={`legislation-title`}>
                  {showContentTypeBadge ? (
                    <div className='mt-1 specific-bottom-margin-12 flex-grow-1 text-light-blue blue-icon content-type-badge flex-centered justify-content-between'>
                      <ContentTypeBadge objectType={item.type} name={'Legislation'} />
                    </div>
                  ) : (
                    <h4 className={`mb-2 content-item-title`}>{item.title}</h4>
                  )}
                  {showContentTypeBadge && <h4 className={`mb-2 content-item-title`}>{item.title}</h4>}
                  <p className={`heading-date mb-1 px-0 ${getAppSite() === 'usa' ? '' : 'information-date'}`}>
                    {getAppSite() === 'usa' ? (
                      <>
                        {!!item.usState && (
                          <>
                            <span className='date'>{item.usState}</span>
                            <span className='dot'></span>
                          </>
                        )}
                        {!!item.usBillNumber && (
                          <>
                            <span className='date'>{item.usBillNumber}</span>
                            <span className='dot'></span>
                          </>
                        )}
                        {!!item.usSession && (
                          <>
                            <span className='date'>{item.usSession}</span>
                          </>
                        )}
                      </>
                    ) : (
                      <>
                        <span className='date'>
                          {item.primaryTypeOfBill}
                          {item.contentType === 'Secondary' && <> legislation</>}
                        </span>
                        {!!item.primarySession && (
                          <>
                            <span className='dot'></span>Session {item.primarySession}
                          </>
                        )}
                      </>
                    )}
                  </p>
                </div>
              </div>
              {!notShowShare && (
                <div className={`px-0 justify-content-end flex-centered`}>
                  {rateUpdatesMode ? (
                    <>
                      {feedItemFlags?.[index]?.importanceRating && (
                        <RatingUpdateBadge
                          item={{ rating: TransformFromPascalCase(feedItemFlags?.[index]?.importanceRating) }}
                        />
                      )}
                    </>
                  ) : (
                    <>
                      {priorityBadgeCondition && <PriorityBadge item={item} feedItemFlags={feedItemFlags[index]} />}
                      <GeneralItemActions
                        item={item}
                        index={index}
                        thumbDownProps={thumbDownProps}
                        itemActionsProps={itemActionsProps}
                      />
                    </>
                  )}
                </div>
              )}
            </div>
            <CurrentStage item={item} />
            {getAppSite() === 'usa' && (
              <div className='legislation-title w-100 px-0'>
                <p className='title-body mt-2 mb-0 position-relative' ref={titleBodyComponent}>
                  <span dangerouslySetInnerHTML={createMarkup(bodySnippet)} />
                </p>
                {item.usDescription && !getUrlParam('search') && !getUrlParam('or') && !getUrlParam('topic-id') && (
                  <p className='title-body mt-2 mb-0'>
                    <span dangerouslySetInnerHTML={createMarkup(item.usDescription)} />
                  </p>
                )}
              </div>
            )}
            {((list !== null && activeSearch && !itemFromWidget) || embedShowTags) && (
              <TagsLists
                explanations={explanations ?? Legislation?.explanations}
                feedItemFlags={feedItemFlags}
                index={index}
                title={'Legislation'}
                section={'Legislation'}
                tagsFixedPosition={tagsFixedPosition}
                scrollBarRef={scrollBarRef}
                onlyEmbargoedTag={onlyEmbargoedTag}
              />
            )}
          </div>
        </Link>
      </li>
    </>
  );
};

const CurrentStage = ({ item }) => {
  const createCurrentStageDate = () => {
    let date;
    if (getAppSite() === 'usa') {
      date = `${item.usLastEventName}`;
    } else {
      if (item.primaryCurrentHouse === 'Royal Assent') {
        date = item.primaryCurrentHouse;
      } else {
        date =
          item.primaryNextEventName !== null
            ? item.primaryNextEventName.replace('Date to be announced', 'Date TBA')
            : '';
      }
    }

    return date;
  };

  const itemClass = () => {
    let className = '';
    if (item.primaryCurrentHouse) {
      switch (item.primaryCurrentHouse) {
        case 'Commons':
          className = 'legislation-commons';
          break;
        case 'Lords':
          className = 'legislation-lords';
          break;
        case 'Royal Assent':
          className = 'legislation-royal';
          break;
        default:
          className = 'legislation-grey';
      }
    }
    if (item.secondaryMadeOrLaid) {
      className = 'legislation-grey';
    }
    return className;
  };
  return (
    <>
      {(item.primaryCurrentHouse || item.usLastEventName) && createCurrentStageDate() && (
        <div className={`current-house-item information-date`}>
          {getAppSite() === 'usa' ? (
            <p className='paragraph-p5' data-item-test-id={'usa-current-stage'}>
              Current stage:{' '}
              <span className={` ${itemClass()}`}>
                {createCurrentStageDate()?.slice(0, 250)}
                {createCurrentStageDate()?.length > 250 ? '...' : ''} (
                {time(item.dateTime, { utc: true, skipTimezone: true }).formatGeneralDate()})
              </span>{' '}
            </p>
          ) : (
            <p>
              Current stage: <span className={` ${itemClass()}`}>{createCurrentStageDate()}</span>
            </p>
          )}
        </div>
      )}
    </>
  );
};

export default LegislationItem;
